import type { CategoryTreeNode } from '../model/types';

/**
 * The platform's default tenant category nesting cap (`catalog.category.max-depth`,
 * epic-catalog-005, adr-catalog-007). This is a **client-side hint only** — the authoritative
 * limit is enforced server-side (`400 CATEGORY_DEPTH_EXCEEDED`); a tenant may have configured a
 * different value, so treat any warning derived from this constant as advisory.
 */
export const CATEGORY_MAX_DEPTH_DEFAULT = 5;

export interface FlatCategoryNode {
  id: string;
  code?: string;
  name: string;
  /** 0 for a top-level (root) category. */
  depth: number;
  parentId: string | null;
  /** Ancestor names, root→parent order (excludes the node itself). */
  ancestorNames: string[];
  localeFallback?: boolean;
}

/** Depth-first (pre-order) flatten of the tenant category tree, in display order. */
export function flattenCategoryTree(nodes: CategoryTreeNode[]): FlatCategoryNode[] {
  const out: FlatCategoryNode[] = [];

  const walk = (
    list: CategoryTreeNode[],
    depth: number,
    parentId: string | null,
    ancestorNames: string[],
  ) => {
    for (const node of list) {
      out.push({
        id: node.id,
        code: node.code,
        name: node.name,
        depth,
        parentId,
        ancestorNames,
        localeFallback: node.localeFallback,
      });
      walk(node.children, depth + 1, node.id, [...ancestorNames, node.name]);
    }
  };

  walk(nodes, 0, null, []);
  return out;
}

/** `{ category id -> ancestor names (root→parent) }`, for a row's breadcrumb secondary text. */
export function buildAncestorNameMap(nodes: CategoryTreeNode[]): Map<string, string[]> {
  return new Map(flattenCategoryTree(nodes).map((node) => [node.id, node.ancestorNames]));
}

/** `{ category id -> depth }` (0 = top-level), for looking up an existing node's own depth. */
export function buildDepthMap(nodes: CategoryTreeNode[]): Map<string, number> {
  return new Map(flattenCategoryTree(nodes).map((node) => [node.id, node.depth]));
}

export interface ParentCategoryOption {
  id: string;
  name: string;
  code?: string;
  depth: number;
}

/**
 * Flattened, tree-ordered list of eligible **parent** options for the category form's parent
 * selector.
 *
 * When editing an existing category (`excludeId` set), the category itself and its *entire*
 * subtree are omitted — a category can never become its own descendant's parent. The server
 * rejects that move with `409 CATEGORY_CYCLE` regardless, but pre-filtering it out of the picker
 * avoids offering a choice that would only round-trip to an error.
 */
export function buildParentOptions(
  nodes: CategoryTreeNode[],
  excludeId?: string | null,
  depth = 0,
): ParentCategoryOption[] {
  const options: ParentCategoryOption[] = [];
  for (const node of nodes) {
    if (excludeId && node.id === excludeId) {
      // Skip the node AND (by not recursing) its entire subtree.
      continue;
    }
    options.push({ id: node.id, name: node.name, code: node.code, depth });
    options.push(...buildParentOptions(node.children, excludeId, depth + 1));
  }
  return options;
}

/**
 * Client-side-only hint that a category placement is at or beyond the tenant's likely maximum
 * nesting depth. NOT authoritative — the server is the source of truth (`400
 * CATEGORY_DEPTH_EXCEEDED`). `parentDepth` is the depth of the chosen parent option
 * (`undefined`/absent means "no parent", i.e. a top-level placement).
 */
export function wouldLikelyExceedMaxDepth(
  parentDepth: number | undefined,
  maxDepth: number = CATEGORY_MAX_DEPTH_DEFAULT,
): boolean {
  const prospectiveDepth = parentDepth === undefined ? 0 : parentDepth + 1;
  return prospectiveDepth >= maxDepth;
}
